// src/components/PrivacyPolicyLite.js
import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNotification } from '../context/NotificationContext';
import { FaShieldAlt, FaExternalLinkAlt, FaCheckCircle } from 'react-icons/fa';
import './PrivacyPolicyLite.css';

const PrivacyPolicyLite = ({ userId }) => {
    const [accepted, setAccepted] = useState(false);
    const [acceptedDate, setAcceptedDate] = useState(null);
    const [loading, setLoading] = useState(true);
    const { showNotification } = useNotification();

    useEffect(() => {
        const loadPrivacyStatus = async () => {
            if (!userId) {
                setLoading(false);
                return;
            }

            try {
                const userRef = doc(db, 'usuarios', userId);
                const userSnap = await getDoc(userRef);

                if (userSnap.exists()) {
                    const data = userSnap.data();
                    setAccepted(data.privacyPolicyAccepted || false);
                    setAcceptedDate(data.privacyPolicyAcceptedDate || null);
                }
            } catch (error) {
                console.error('Error loading privacy policy status:', error);
            } finally {
                setLoading(false);
            }
        };

        loadPrivacyStatus();
    }, [userId]);

    const handleAcceptanceChange = async (e) => {
        const isAccepted = e.target.checked;
        setAccepted(isAccepted);

        if (!userId) {
            showNotification('No se pudo guardar la preferencia. Usuario no identificado.', 'error');
            return;
        }

        try {
            const userRef = doc(db, 'usuarios', userId);
            const updateData = {
                privacyPolicyAccepted: isAccepted,
            };

            if (isAccepted) {
                const now = new Date().toISOString();
                updateData.privacyPolicyAcceptedDate = now;
                setAcceptedDate(now);
            }

            await updateDoc(userRef, updateData);

            showNotification(
                isAccepted
                    ? 'Has aceptado el Aviso de Privacidad.'
                    : 'Has retirado tu aceptación del Aviso de Privacidad.',
                'success'
            );
        } catch (error) {
            console.error('Error updating privacy policy acceptance:', error);
            showNotification('Error al guardar tu preferencia. Intenta de nuevo.', 'error');
            // Revertir el estado si falla
            setAccepted(!isAccepted);
        }
    };

    const handleViewFullPolicy = () => {
        window.open('/aviso-privacidad', '_blank');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const [isCollapsed, setIsCollapsed] = useState(true);

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    if (loading) {
        return (
            <div className="privacy-policy-lite">
                <p>Cargando...</p>
            </div>
        );
    }

    return (
        <div className={`privacy-policy-lite ${isCollapsed ? 'collapsed' : 'expanded'}`}>
            <div
                className="privacy-header collapsible-header"
                onClick={toggleCollapse}
                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaShieldAlt className="privacy-icon" />
                    <h3>Aviso de Privacidad</h3>
                </div>
                <span className="collapse-icon" style={{ fontSize: '0.9rem', color: '#64748b' }}>
                    {isCollapsed ? '▼' : '▲'}
                </span>
            </div>

            {!isCollapsed && (
                <>
                    <div className="privacy-content">
                        <p>
                            <strong>Domus Habita Soluciones Tecnológicas</strong> es responsable del uso y protección
                            de los datos personales que se recaben a través de su plataforma digital, en cumplimiento
                            con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares.
                        </p>

                        <p>
                            Los datos personales recabados incluyen datos de identificación, contacto, información
                            administrativa y financiera, registros de accesos, así como <strong>archivos multimedia
                                (fotos, videos y audio)</strong> que el usuario decida capturar de forma voluntaria.
                        </p>

                        <p>
                            Estos datos son utilizados para la prestación de servicios, seguridad, administración
                            y <strong>gestión de evidencias</strong> en incidencias, además de permitir el guardado
                            de códigos QR en la galería del dispositivo.
                        </p>

                        <p>
                            Domus Habita no comparte ni transfiere datos personales a terceros, salvo en los casos
                            legalmente permitidos o cuando sea necesario para el cumplimiento de los servicios contratados.
                        </p>

                        <p>
                            El titular podrá ejercer sus derechos de Acceso, Rectificación, Cancelación u Oposición
                            (Derechos ARCO) enviando una solicitud al correo electrónico{' '}
                            <a href="mailto:domushabitat2025@gmail.com" className="privacy-email">
                                domushabitat2025@gmail.com
                            </a>.
                        </p>
                    </div>

                    <div className="privacy-actions">
                        <label className="privacy-checkbox-label">
                            <input
                                type="checkbox"
                                checked={accepted}
                                onChange={handleAcceptanceChange}
                                className="privacy-checkbox"
                            />
                            <span className="checkbox-text">
                                He leído y acepto el Aviso de Privacidad de Domus Habita Soluciones Tecnológicas.
                            </span>
                        </label>

                        {accepted && acceptedDate && (
                            <div className="privacy-accepted-info">
                                <FaCheckCircle className="check-icon" />
                                <span>Aceptado el {formatDate(acceptedDate)}</span>
                            </div>
                        )}

                        <button
                            onClick={handleViewFullPolicy}
                            className="privacy-full-link"
                            type="button"
                        >
                            <FaExternalLinkAlt />
                            <span>Ver Aviso de Privacidad Completo</span>
                        </button>
                    </div>
                </>
            )}
        </div>
    );

};

export default PrivacyPolicyLite;
